import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { GUIDES, type Finder, type Step, navHops, pathOf, searchOf, trackForPath } from "../lib/help";
import { entityLabel, resolve } from "../lib/nav";

/**
 * In-app help steps (requirement 9.10): a self-guided walkthrough for every track, one per business problem — for a person
 * learning the screen on their own, not only for someone demonstrating it. Which track a page belongs to, what its steps
 * are and how each step's target is found live in `lib/help.ts`; the step texts live in the i18n bundles
 * (`help.<track>.sN`). This file is the drawer and the marker: a pull-out panel docked on the right that the person keeps
 * open while working the page (the page shifts left, nothing is hidden), resized by its edge, with the pull tab left on
 * the screen edge when it is closed. Progress, open state and width are per-browser conveniences.
 *
 * "Show me" never navigates: it puts a marker on the element to click, on the last hop of the step that is visible here,
 * and the marker follows the person as the page changes. Off the step's page it points at the way there — the module's
 * own sub-navigation when the reader is already inside the module, otherwise the hub card or the ☰ Modules menu.
 */
export { trackForPath } from "../lib/help";

const MIN_W = 320;
const MAX_W = 760;
const DEFAULT_W = 440;
const OWN = "[data-help-guide-panel], [data-help-guide-marker], [data-help-guide-tab]";

function readDone(track: string): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(`help-guide-${track}`) || "{}") as Record<string, boolean>; } catch { return {}; }
}
function readPref<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v == null ? fallback : (JSON.parse(v) as T); } catch { return fallback; }
}
function writePref(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private window */ }
}

// ---------------------------------------------------------------- finding the element to mark
function visible(el: Element | null | undefined): el is HTMLElement {
  if (!el) return false;
  const r = (el as HTMLElement).getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}
function ours(el: Element): boolean {
  return !!el.closest(OWN);
}
function norm(s: string): string {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}
/** The first visible element a finder describes; text finders compare against the translated label (exact, then prefix). */
function findOne(f: Finder, t: (k: string) => string): HTMLElement | null {
  let el: HTMLElement | null = null;
  if (f.sel) {
    el = Array.from(document.querySelectorAll<HTMLElement>(f.sel)).find((e) => !ours(e) && visible(e)) ?? null;
  } else if (f.text) {
    const label = norm(t(f.text));
    if (!label) return null;
    const cands = Array.from(document.querySelectorAll<HTMLElement>(f.role ? `[role="${f.role}"]` : f.tag ?? "button, a, h2, summary")).filter((e) => !ours(e) && visible(e));
    const texts = cands.map((e) => norm(e.innerText || e.textContent || ""));
    let i = texts.findIndex((s) => s === label);
    if (i < 0) i = texts.findIndex((s) => s.startsWith(label));
    if (i < 0) i = texts.findIndex((s) => s.includes(label));
    el = i >= 0 ? cands[i] : null;
  }
  if (el && f.then) {   // the nearest container around the anchor that holds a match for `then`
    let node: HTMLElement | null = el;
    let hit: HTMLElement | null = null;
    while (node && !hit) { hit = node.querySelector<HTMLElement>(f.then); node = node.parentElement; }
    el = hit && visible(hit) ? hit : null;
  }
  return el;
}
type Mark = { el: HTMLElement; hop: Finder; final: boolean; nav: boolean };
function locate(step: Step, pathname: string, t: (k: string) => string): Mark | null {
  const onPage = pathname === pathOf(step.to) || pathname.startsWith(pathOf(step.to) + "/");
  if (onPage) {
    for (let i = step.hops.length - 1; i >= 0; i--) {
      const el = findOne(step.hops[i], t);
      if (el) return { el, hop: step.hops[i], final: i === step.hops.length - 1, nav: false };
    }
    return null;
  }
  const hops = navHops(step.to, pathname);
  for (let i = hops.length - 1; i >= 0; i--) {
    const el = findOne(hops[i], t);
    if (el) return { el, hop: hops[i], final: false, nav: !hops[i].inpage };
  }
  return null;
}

/** The pulsing ring, the dimmed page around it and the caption, following the element on scroll and resize. */
function Marker({ mark, n, caption, onHide }: { mark: Mark; n: number; caption: string; onHide: () => void }) {
  const [rect, setRect] = useState(() => mark.el.getBoundingClientRect());
  useEffect(() => {
    let raf = 0;
    const update = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setRect(mark.el.getBoundingClientRect())); };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    const id = window.setInterval(update, 300);
    return () => { window.removeEventListener("scroll", update, true); window.removeEventListener("resize", update); window.clearInterval(id); cancelAnimationFrame(raf); };
  }, [mark.el]);
  const pad = 6;
  const above = rect.top > 44;
  return createPortal(
    <div data-help-guide-marker data-help-guide-marker-final={mark.final ? "1" : "0"} data-help-guide-marker-nav={mark.nav ? "1" : "0"}>
      <div aria-hidden style={{ position: "fixed", left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, zIndex: 45, pointerEvents: "none" }}
        className="rounded-lg ring-4 ring-brand-red shadow-[0_0_0_9999px_rgba(20,30,50,0.28)] animate-pulse" />
      <div style={{ position: "fixed", left: Math.max(8, rect.left - pad), top: above ? rect.top - pad - 30 : rect.bottom + pad + 4, zIndex: 46 }}
        className="flex items-center gap-2 rounded-full bg-brand-red px-3 py-1 text-xs font-semibold text-white shadow-lg">
        <span>{n}</span><span aria-hidden>→</span><span>{caption}</span>
        <button type="button" onClick={onHide} aria-label="hide marker" className="ml-1 rounded-full bg-white/20 px-1.5 leading-none hover:bg-white/40">✕</button>
      </div>
    </div>, document.body);
}

export function HelpGuideButton() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const track = trackForPath(pathname);
  const [open, setOpen] = useState<boolean>(() => readPref("help-guide-open", false));
  const [width, setWidth] = useState<number>(() => Math.min(MAX_W, Math.max(MIN_W, readPref("help-guide-width", DEFAULT_W))));
  const [done, setDone] = useState<Record<string, boolean>>({});
  // the active step survives the remount that every page change causes (each page renders its own shell)
  const [active, setActiveState] = useState<string | null>(() => { const v = readPref<{ track: string; key: string } | null>("help-guide-active", null); return v && v.track === track ? v.key : null; });
  const setActive = useCallback((key: string | null) => { setActiveState(key); writePref("help-guide-active", key && track ? { track, key } : null); }, [track]);
  const [mark, setMark] = useState<Mark | null>(null);
  const drag = useRef<{ startX: number; startW: number } | null>(null);
  const lastEl = useRef<HTMLElement | null>(null);
  useEffect(() => { if (track) setDone(readDone(track)); const v = readPref<{ track: string; key: string } | null>("help-guide-active", null); setActiveState(v && v.track === track ? v.key : null); setMark(null); }, [track]);
  // the page shifts left by the drawer width so nothing is hidden behind it
  useEffect(() => {
    const shift = track && open ? `${width}px` : "";
    document.body.style.paddingRight = shift;
    document.body.style.transition = "padding-right 150ms ease";
    return () => { document.body.style.paddingRight = ""; };
  }, [track, open, width]);
  const onDrag = useCallback((e: MouseEvent) => {
    if (!drag.current) return;
    const w = Math.min(MAX_W, Math.max(MIN_W, drag.current.startW + (drag.current.startX - e.clientX)));
    setWidth(w);
  }, []);
  const endDrag = useCallback(() => { if (drag.current) { drag.current = null; setWidth((w) => { writePref("help-guide-width", w); return w; }); } window.removeEventListener("mousemove", onDrag); window.removeEventListener("mouseup", endDrag); }, [onDrag]);
  const startDrag = (e: React.MouseEvent) => { drag.current = { startX: e.clientX, startW: width }; window.addEventListener("mousemove", onDrag); window.addEventListener("mouseup", endDrag); e.preventDefault(); };
  const guide = track ? GUIDES[track] : null;
  const step = guide?.steps.find((s) => s.key === active) ?? null;
  const tick = useCallback((key: string, value: boolean) => {
    if (!track) return;
    setDone((d) => { const next = { ...d, [key]: value }; try { localStorage.setItem(`help-guide-${track}`, JSON.stringify(next)); } catch { /* private window */ } return next; });
  }, [track]);
  // the marker follows the presenter: re-locate the step's target as the page, the tab or the data change
  useEffect(() => {
    if (!step) { setMark(null); lastEl.current = null; return; }
    const run = () => {
      const m = locate(step, pathname, t);
      setMark((prev) => (prev && m && prev.el === m.el && prev.final === m.final ? prev : m));
      if (m && m.el !== lastEl.current) { lastEl.current = m.el; m.el.scrollIntoView({ block: "center", behavior: "smooth" }); }
    };
    run();
    const id = window.setInterval(run, 400);
    return () => window.clearInterval(id);
  }, [step, pathname, t, i18n.language]);
  // a click on the marked target: the last hop ticks the step; earlier hops (a tab, a row, the way there) keep the marker moving
  useEffect(() => {
    if (!mark) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || ours(target) || !mark.el.contains(target)) return;
      if (mark.final && !mark.hop.look && step) { tick(step.key, true); setActive(null); }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setActive(null); };
    document.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("click", onClick, true); window.removeEventListener("keydown", onKey); };
  }, [mark, step, tick]);
  if (!track || !guide) return null;
  const setOpenPersist = (v: boolean) => { setOpen(v); writePref("help-guide-open", v); };
  const reset = () => { setDone({}); setActive(null); try { localStorage.removeItem(`help-guide-${track}`); } catch { /* ignore */ } };
  const n = guide.steps.filter((s) => done[s.key]).length;
  const nextIdx = guide.steps.findIndex((s) => !done[s.key]);
  const stepIndex = step ? guide.steps.indexOf(step) + 1 : 0;
  const caption = (() => {
    if (!mark || !step) return "";
    if (mark.nav) {
      const r = resolve(pathOf(step.to), searchOf(step.to));
      const where = r ? [t(r.module.label), r.entity ? entityLabel(r.entity, i18n.language, t) : null].filter(Boolean).join(" › ") : step.to;
      return t("help.navigate", { where });
    }
    return mark.hop.look ? t("help.lookHere") : t("help.clickHere");
  })();
  return (
    <>
      <button type="button" onClick={() => setOpenPersist(!open)} data-help-guide aria-expanded={open} className="whitespace-nowrap rounded-md border border-brand-grey-light px-3 py-1.5 text-xs font-semibold text-brand-charcoal hover:border-brand-primary">
        ▶ {t("help.button")}{n > 0 && <span className="ml-1 text-brand-grey">{n}/{guide.steps.length}</span>}
      </button>
      {mark && step && <Marker mark={mark} n={stepIndex} caption={caption} onHide={() => setActive(null)} />}
      {!open && createPortal(
        <button type="button" onClick={() => setOpenPersist(true)} data-help-guide-tab title={t("help.pull")}
          className="fixed right-0 top-1/2 z-40 -translate-y-1/2 rounded-l-md border border-r-0 border-brand-primary/40 bg-white px-1.5 py-3 text-[11px] font-semibold text-brand-primary shadow-md hover:bg-blue-50"
          style={{ writingMode: "vertical-rl", transform: "translateY(-50%) rotate(180deg)" }}>
          ▶ {t("help.button")} {n > 0 ? `${n}/${guide.steps.length}` : ""}
        </button>, document.body)}
      {open && createPortal(
        <aside data-help-guide-panel role="complementary" aria-label={t("help.title")} style={{ width }}
          className="fixed right-0 top-0 z-40 flex h-full flex-col border-l border-brand-grey-light bg-white shadow-2xl">
          <div onMouseDown={startDrag} title={t("help.resize")} data-help-guide-resize className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-brand-primary/30" />
          <div className="flex items-start justify-between gap-2 border-b border-brand-grey-light px-5 py-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("help.title")} · {t(`help.${track}.name`)}</div>
              <div className="mt-1 text-xs text-brand-grey">{t("help.minutes", { n: guide.minutes })} · {t("help.hint")}</div>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setWidth((w) => { const v = w >= MAX_W ? DEFAULT_W : MAX_W; writePref("help-guide-width", v); return v; })} title={t("help.resize")} className="rounded px-2 py-1 text-brand-grey hover:bg-brand-grey-light">⇔</button>
              <button type="button" onClick={() => setOpenPersist(false)} aria-label="close" title={t("help.close")} className="rounded px-2 py-1 text-brand-grey hover:bg-brand-grey-light">✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="rounded-lg border-l-4 border-brand-primary bg-blue-50/60 px-3 py-2 text-sm">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-primary">{t("help.problem")}</div>
              <div className="mt-0.5 text-brand-charcoal">{t(`help.${track}.problem`)}</div>
            </div>
            <ol className="mt-4 space-y-2 text-sm">
              {guide.steps.map((s, i) => {
                const isActive = active === s.key;
                return (
                  <li key={s.key} data-help-guide-step={s.key} data-help-guide-active={isActive ? "1" : "0"} className={`rounded border px-3 py-2 ${done[s.key] ? "border-status-green/40 bg-green-50/40" : isActive ? "border-brand-red bg-red-50/40" : i === nextIdx ? "border-brand-primary bg-blue-50/30" : "border-brand-grey-light"}`}>
                    <div className="flex items-start gap-2">
                      <input type="checkbox" checked={!!done[s.key]} onChange={() => tick(s.key, !done[s.key])} className="mt-1" aria-label={`step ${i + 1}`} />
                      <div className="flex-1 leading-snug">
                        <span className="mr-1 font-semibold text-brand-grey">{i + 1}.</span>{t(`help.${track}.${s.key}`)}
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          {isActive
                            ? <button type="button" onClick={() => setActive(null)} data-help-guide-hide className="rounded border border-brand-red bg-brand-red px-2 py-0.5 font-semibold text-white hover:bg-red-700">{t("help.hide")}</button>
                            : <button type="button" onClick={() => setActive(s.key)} data-help-guide-show className="rounded border border-brand-primary px-2 py-0.5 font-semibold text-brand-primary hover:bg-blue-50">◎ {t("help.show")}</button>}
                          <span className="text-brand-grey">{s.to}</span>
                          {isActive && !mark && <span className="text-brand-red" data-help-guide-no-target>{t("help.noTarget")}</span>}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
          <div className="flex items-center justify-between border-t border-brand-grey-light px-5 py-3 text-xs text-brand-grey">
            <span>{n}/{guide.steps.length} {t("help.done")}</span>
            <button type="button" onClick={reset} className="rounded border border-brand-grey-light px-2 py-1 hover:border-brand-primary">{t("help.reset")}</button>
          </div>
        </aside>, document.body)}
    </>
  );
}
